import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { isEmpty } from "lodash";
import useToast from "../../../hooks/useToast";
import { TOAST } from "../../../constants/ToastMessages";
import useFetch from "../../../hooks/useFetch";
import usePostRequest from "../../../hooks/usePostRequest";
import useApiCall from "../../../hooks/useApiCall";
import useTableSorting from "../../../hooks/useTableSorting";
import {
  convertUnderscoreToSpaces,
  formatDate,
  getStringParamsFromObject,
  exportToCSV,
  getStatusChipProps,
  parseUrl,
} from "../../../utils";
import SearchInput from "../../../components/SearchInput";
import OptionMenu from "../../../components/OptionMenu";
import ChunksSidebar from "./ChunksSidebar";
import {
  WEBSITE_FILTER_OPTIONS,
  SITEMAP_REFRESH_REVIEW_STATUS,
} from "../../../constants/Constants";
import { INDIGO, SLATE } from "../../../constants/Colors";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { URL_COL_TEMPLATE, URL_COLUMN_HEADERS } from "./urlSidebarConstants";
import useUrlBulkActions from "../../../hooks/useUrlBulkActions";
import ExportConfirmDialog from "./ExportConfirmDialog";
import UrlTableRow from "./UrlTableRow";

export default function UrlsSidebar({ website, currentApp, tokens, isOpen, onClose, readOnly = false }) {
  const { error } = useToast();
  const [urls, setUrls] = useState({});
  const [selectedChunkUrl, setSelectedChunkUrl] = useState(null);
  const [isChunksSidebarOpen, setIsChunksSidebarOpen] = useState(false);
  const [isAddLinkBoxOpen, setIsAddLinkBoxOpen] = useState(false);
  const [pendingUrls, setPendingUrls] = useState([]);
  const [urlInput, setUrlInput] = useState("");
  const [search, setSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const [statusFilters, setStatusFilters] = useState(
    WEBSITE_FILTER_OPTIONS.map((el) => ({
      title: convertUnderscoreToSpaces(el),
      value: el,
      selected: false,
      dotColor: getStatusChipProps(el).sx?.color,
      onClick: () => {
        setStatusFilters((prev) => {
          const next = [...prev];
          const i = next.findIndex((f) => f.value === el);
          if (i !== -1) next[i] = { ...next[i], selected: !next[i].selected };
          return next;
        });
      },
    }))
  );

  const [sitemapReviewFilters, setSitemapReviewFilters] = useState(
    SITEMAP_REFRESH_REVIEW_STATUS.map((el) => ({
      title: convertUnderscoreToSpaces(el),
      value: el,
      selected: false,
      onClick: () => {
        setSitemapReviewFilters((prev) => {
          const next = [...prev];
          const i = next.findIndex((f) => f.value === el);
          if (i !== -1) next[i] = { ...next[i], selected: !next[i].selected };
          return next;
        });
      },
    }))
  );

  const {
    paginationParams,
    setPaginationParams,
    handleRequestSort,
    onChangeRowsPerPage,
    onChangePage,
  } = useTableSorting(
    { page_size: 25, page: 0, order_by: "url", order: "asc" },
    null
  );

  const [currentUrl, setCurrentUrl] = useState("");
  const exportCallMade = useRef(false);
  const refreshDaysRef = useRef(null);
  const tagRequestSeq = useRef({});

  const { data: urlData, loading, fetchData } = useFetch(
    `/v1/apps/${currentApp.id}/websites/${website.id}/urls`,
    tokens,
    [],
    [],
    false
  );

  const { sendPostRequest, loading: insertingUrls } = usePostRequest(
    `/v1/apps/${currentApp.id}/websites/${website.id}/urls/bulk_create`,
    tokens
  );

  const { makeRequest } = useApiCall();

  const updateUrl = (url, body, { ignoreError = false } = {}) =>
    makeRequest(`/v1/apps/${currentApp.id}/websites/${website.id}/urls/${url.id}`, {
      method: "PUT",
      authTokens: tokens,
      body,
      ignore_error: ignoreError,
    });

  const { sendPostRequest: sendUpdateStatusRequest } = usePostRequest(
    `/v1/apps/${currentApp.id}/websites/${website.id}/urls/update_status`,
    tokens
  );

  const { data: exportData, fetchData: fetchExportData } = useFetch(
    `/v1/apps/${currentApp.id}/websites/${website.id}/urls`,
    tokens,
    [],
    [],
    false
  );

  const buildFilterUrl = (params, pageSize) => {
    const p = pageSize != null ? { ...params, page_size: pageSize } : { ...params };
    let url = `/v1/apps/${currentApp.id}/websites/${website.id}/urls?${getStringParamsFromObject(p)}&search=${search.trim()}`;
    const statusStr = statusFilters.filter((s) => s.selected).map((s) => s.value).join(",");
    const sitemapStr = sitemapReviewFilters.filter((s) => s.selected).map((s) => s.value).join(",");
    if (!isEmpty(statusStr)) url += "&status=" + statusStr;
    if (!isEmpty(sitemapStr)) url += "&sitemap_refresh_review_status=" + sitemapStr;
    return url;
  };

  const handleUrlInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = urlInput.trim();
      if (val && !pendingUrls.includes(val)) {
        setPendingUrls((prev) => [...prev, val]);
        setUrlInput("");
      }
    }
  };

  const handleUrlInputPaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    const lines = pasted.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length > 1) {
      e.preventDefault();
      setPendingUrls((prev) => {
        const existing = new Set(prev);
        const newUrls = lines.filter((l) => !existing.has(l));
        return [...prev, ...newUrls];
      });
      setUrlInput("");
    }
    // single URL paste — let it land in the input normally
  };

  const handleAddLinks = async () => {
    const current = urlInput.trim();
    const allUrls = current && !pendingUrls.includes(current)
      ? [...pendingUrls, current]
      : pendingUrls;
    if (!allUrls.length) return;
    const response = await sendPostRequest({ urls: allUrls });
    if (response.message === "Ok") {
      fetchData(currentUrl, tokens);
      setPendingUrls([]);
      setUrlInput("");
      setIsAddLinkBoxOpen(false);
    }
  };

  const handleOnRefreshDaysChange = (url, e) => {
    const days = parseInt(e.target.value);
    if (days <= 0 || days > 365) {
      error(TOAST.websites.refreshDaysError);
      return;
    }
    // useApiCall already surfaces the error via toast; swallow to avoid an unhandled rejection.
    updateUrl(url, { url: { refresh_days: days } }).catch(() => {});
  };

  const handleTagsChange = async (url, newTags) => {
    const previous = url.tags || [];
    const seq = (tagRequestSeq.current[url.id] || 0) + 1;
    tagRequestSeq.current[url.id] = seq;

    setUrls((prev) => ({
      ...prev,
      data: prev.data.map((u) => (u.id === url.id ? { ...u, tags: newTags } : u)),
    }));

    try {
      await updateUrl(url, { url: { tags: newTags } }, { ignoreError: true });
    } catch (err) {
      // A later edit to this row superseded this request — its own result wins.
      if (tagRequestSeq.current[url.id] !== seq) return;

      setUrls((prev) => ({
        ...prev,
        data: prev.data.map((u) => (u.id === url.id ? { ...u, tags: previous } : u)),
      }));
      error(TOAST.websites.tagsUpdateError);
    }
  };

  const bulkUpdateUrls = async (status, extra = {}) => {
    try {
      const response = await sendUpdateStatusRequest({
        status,
        url_ids: selectedUrls,
        ...extra,
      });
      if (response) {
        await new Promise((r) => setTimeout(r, 500));
        const url = buildFilterUrl(paginationParams);
        setCurrentUrl(url);
        await fetchData(url, tokens);
        setSelectedUrls([]);
      }
    } catch (err) {
      console.error("Error updating URLs:", err);
    }
  };

  const handleExportClick = () => setExportDialogOpen(true);

  const handleExportConfirm = () => {
    setExportDialogOpen(false);
    setIsExporting(true);
  };

  // Effects
  useEffect(() => {
    const url = buildFilterUrl(paginationParams);
    setCurrentUrl(url);
    fetchData(url, tokens);
  }, [paginationParams, search, statusFilters, sitemapReviewFilters]); // eslint-disable-line

  useEffect(() => {
    if (urlData) setUrls(urlData);
  }, [urlData]);

  useEffect(() => {
    if (exportData?.data && isExporting && exportCallMade.current) {
      const tz = new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2];
      const csvData = exportData.data.map((u) => ({
        url: u.url,
        title: u.title || "",
        status: convertUnderscoreToSpaces(u.status),
        sitemap_refresh_review_status: convertUnderscoreToSpaces(u.sitemap_refresh_review_status),
        metadata: u.metadata ? JSON.stringify(u.metadata) : "",
        tags: (u.tags || []).join(", "),
        indexed_at: u.indexed_at ? formatDate(u.indexed_at) : "",
        created_at: formatDate(u.created_at),
        updated_at: formatDate(u.updated_at),
      }));
      const headers = ["url", "title", "status", "sitemap_refresh_review_status", "metadata", "tags",
        `indexed_at (${tz})`, `created_at (${tz})`, `updated_at (${tz})`];
      const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      exportToCSV(csvData, `urls_export_${website.url.replace(/[^a-zA-Z0-9]/g, "_")}_${ts}.csv`, headers);
      setIsExporting(false);
      exportCallMade.current = false;
    }
  }, [exportData]); // eslint-disable-line

  useEffect(() => {
    if (isExporting) {
      const url = buildFilterUrl(paginationParams, 10000);
      exportCallMade.current = true;
      fetchExportData(url, tokens);
    }
  }, [isExporting]); // eslint-disable-line

  useEffect(() => {
    setPaginationParams((prev) => ({ ...prev, page: 0 }));
  }, [search, statusFilters, sitemapReviewFilters]); // eslint-disable-line

  const activeFilterCount =
    statusFilters.filter((f) => f.selected).length +
    sitemapReviewFilters.filter((f) => f.selected).length;

  const urlList = urls?.data || [];
  const { selectedUrls, setSelectedUrls, handleCheckboxClick, handleSelectAllClick, allSelected, someSelected } = useUrlBulkActions(urlList);

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 21474834 }}
        PaperProps={{
          sx: {
            width: "70%",
            minWidth: 640,
            maxWidth: 1100,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: SLATE[50],
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            bgcolor: "#ffffff",
            borderBottom: `1px solid ${SLATE[200]}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1, mr: 2 }}>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.08em", mb: "8px" }}>
              Knowledge Base — URLs
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flexWrap: "wrap" }}>
              <LinkIcon sx={{ color: INDIGO[500], fontSize: "1.125rem", flexShrink: 0 }} />
              <Typography
                component="a"
                href={website.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: SLATE[900],
                  textDecoration: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": { color: INDIGO[600], textDecoration: "underline" },
                }}
              >
                {website.url}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: "0.875rem", color: SLATE[400], flexShrink: 0 }} />
              {urls?.total != null && (
                <Box sx={{ flexShrink: 0, px: "10px", py: "2px", bgcolor: SLATE[100], borderRadius: "20px" }}>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[600] }}>
                    {urls.total.toLocaleString()} URLs
                  </Typography>
                </Box>
              )}
              <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], flexShrink: 0 }}>
                Updated {formatDate(website.updated_at)}
              </Typography>
            </Box>
          </Box>

          <IconButton size="small" onClick={onClose} sx={{ color: SLATE[400], "&:hover": { color: SLATE[700], bgcolor: SLATE[100] }, mt: "2px" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: "#ffffff",
            borderBottom: `1px solid ${SLATE[200]}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchInput value={search} onChange={(value) => setSearch(value)} placeholder="Search URLs..." />
          </Box>

          {/* Filter button */}
          <OptionMenu
            enableCheckBoxes
            zIndex={21474836}
            options={[
              { title: "Status", isHeader: true, disabled: true },
              ...statusFilters,
              { title: "Sitemap Review Status", isHeader: true, disabled: true },
              ...sitemapReviewFilters,
            ]}
            customStyle={{ width: "40ch" }}
            button={
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: activeFilterCount > 0 ? INDIGO[300] : SLATE[300],
                  color: activeFilterCount > 0 ? INDIGO[600] : SLATE[600],
                  bgcolor: activeFilterCount > 0 ? INDIGO[50] : "transparent",
                  textTransform: "none",
                  borderRadius: "8px",
                  gap: 0.75,
                  "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50] },
                }}
              >
                <FilterIcon sx={{ fontSize: "1rem" }} />
                Filters
                {activeFilterCount > 0 && (
                  <Box sx={{ bgcolor: INDIGO[600], color: "#fff", borderRadius: "10px", px: "6px", fontSize: "0.6875rem", fontWeight: 700, lineHeight: "18px" }}>
                    {activeFilterCount}
                  </Box>
                )}
              </Button>
            }
          />

          {/* Add Links — hidden in read-only */}
          {!readOnly && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsAddLinkBoxOpen(true)}
              sx={{
                borderColor: SLATE[300],
                color: SLATE[700],
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50], color: INDIGO[600] },
              }}
            >
              + Links
            </Button>
          )}

          {/* Bulk Actions — hidden in read-only */}
          {!readOnly && (
            <OptionMenu
              options={[
                { title: "Index", onClick: () => bulkUpdateUrls("to_be_indexed"), value: "index" },
                { title: "Force Index", onClick: () => bulkUpdateUrls("to_be_indexed", { force_index: true }), value: "force_index" },
                { title: "Skip", onClick: () => bulkUpdateUrls("to_be_skipped"), value: "skip" },
                { title: "Delete", onClick: () => bulkUpdateUrls("to_be_deleted"), value: "delete" },
              ]}
              button={
                <Button
                  variant="outlined"
                  size="small"
                  disabled={selectedUrls.length === 0}
                  sx={{
                    borderColor: SLATE[300],
                    color: SLATE[700],
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50], color: INDIGO[600] },
                    "&.Mui-disabled": { borderColor: SLATE[200], color: SLATE[400] },
                  }}
                >
                  Bulk Actions
                  {selectedUrls.length > 0 && (
                    <Box sx={{ bgcolor: INDIGO[600], color: "#fff", borderRadius: "10px", px: "6px", fontSize: "0.6875rem", fontWeight: 700, lineHeight: "18px", ml: 0.75 }}>
                      {selectedUrls.length}
                    </Box>
                  )}
                </Button>
              }
              zIndex={21474836}
            />
          )}

          {/* Export */}
          <Tooltip
            title={
              activeFilterCount > 0 || search.trim()
                ? `Exports ${urls?.count?.toLocaleString() ?? "…"} filtered URLs (up to 10,000) — filtered from ${urls?.total?.toLocaleString() ?? "…"} total`
                : `Exports all ${urls?.total?.toLocaleString() ?? "…"} URLs (up to 10,000)`
            }
            placement="bottom-end"
            PopperProps={{ sx: { zIndex: 21474837 } }}
          >
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={handleExportClick}
                disabled={isExporting || urlList.length === 0}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  boxShadow: "none",
                  gap: 0.75,
                  "&:hover": { boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: SLATE[200], color: SLATE[400] },
                }}
              >
                {isExporting ? (
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                ) : (
                  <DownloadIcon sx={{ fontSize: "1rem" }} />
                )}
                {isExporting ? "Exporting…" : "Export CSV"}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Add Links box */}
        {isAddLinkBoxOpen && (
          <Box sx={{ px: 3, py: 2.5, bgcolor: "#ffffff", borderBottom: `1px solid ${SLATE[200]}`, flexShrink: 0 }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LinkIcon sx={{ fontSize: "1rem", color: INDIGO[500] }} />
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[800] }}>
                  Add URLs
                </Typography>
              </Box>
              {pendingUrls.length > 0 && (
                <Box sx={{ px: "10px", py: "2px", bgcolor: INDIGO[50], border: `1px solid ${INDIGO[100]}`, borderRadius: "20px" }}>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: INDIGO[600] }}>
                    {pendingUrls.length} queued
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Confirmed URL chips */}
            {pendingUrls.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 1.5, p: 1.5, bgcolor: SLATE[50], borderRadius: "8px", border: `1px solid ${SLATE[200]}` }}>
                {pendingUrls.map((url) => {
                  const { domain, path } = parseUrl(url);
                  return (
                    <Box
                      key={url}
                      sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "10px", py: "4px", bgcolor: "#ffffff", border: `1px solid ${INDIGO[200]}`, borderRadius: "6px", maxWidth: "100%" }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography component="span" sx={{ fontSize: "0.6875rem", color: SLATE[400] }}>{domain}</Typography>
                        <Typography component="span" sx={{ fontSize: "0.6875rem", color: INDIGO[600], fontWeight: 500 }}>{path === "/" ? "/" : `/${path.replace(/^\//, "")}`}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setPendingUrls((prev) => prev.filter((u) => u !== url))}
                        sx={{ p: 0, ml: "2px", color: SLATE[400], "&:hover": { color: SLATE[700] } }}
                      >
                        <CloseIcon sx={{ fontSize: "0.75rem" }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* URL input */}
            <TextField
              fullWidth
              autoFocus
              disabled={insertingUrls}
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={handleUrlInputKeyDown}
              onPaste={handleUrlInputPaste}
              placeholder="Type a URL and press Enter, or paste multiple at once"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "0.8125rem",
                  bgcolor: SLATE[50],
                  fontFamily: "ui-monospace, 'Cascadia Code', Menlo, Consolas, monospace",
                  "& fieldset": { borderColor: SLATE[200] },
                  "&:hover fieldset": { borderColor: SLATE[300] },
                  "&.Mui-focused fieldset": { borderColor: INDIGO[400] },
                },
                "& .MuiInputBase-input": { color: SLATE[700], py: "10px" },
              }}
            />
            <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400], mt: "5px" }}>
              Press <Box component="kbd" sx={{ px: "5px", py: "1px", bgcolor: SLATE[100], border: `1px solid ${SLATE[300]}`, borderRadius: "4px", fontSize: "0.6875rem", fontFamily: "monospace" }}>Enter</Box> to add one by one, or paste a list of URLs to add them all at once. Duplicates are ignored automatically.
            </Typography>

            {/* Actions */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => { setIsAddLinkBoxOpen(false); setPendingUrls([]); setUrlInput(""); }}
                sx={{ borderColor: SLATE[300], color: SLATE[600], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={(pendingUrls.length === 0 && !urlInput.trim()) || insertingUrls}
                onClick={handleAddLinks}
                sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", minWidth: 80, "&:hover": { boxShadow: "none" }, "&.Mui-disabled": { bgcolor: SLATE[200], color: SLATE[400] } }}
              >
                {insertingUrls ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <CircularProgress size={12} sx={{ color: "#fff" }} />
                    Adding…
                  </Box>
                ) : (
                  `Add ${pendingUrls.length + (urlInput.trim() ? 1 : 0)} URL${pendingUrls.length + (urlInput.trim() ? 1 : 0) === 1 ? "" : "s"}`
                )}
              </Button>
            </Box>
          </Box>
        )}

        {/* Filtered count */}
        {(search.trim() !== "" || statusFilters.some((f) => f.selected)) && !loading && urls?.count != null && (
          <Box sx={{ px: 3, py: 1, bgcolor: INDIGO[50], borderBottom: `1px solid ${INDIGO[100]}` }}>
            <Typography sx={{ fontSize: "0.8125rem", color: INDIGO[700] }}>
              Showing {urls.count} filtered {urls.count === 1 ? "URL" : "URLs"}
            </Typography>
          </Box>
        )}

        {/* Table */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Box sx={{ bgcolor: "#ffffff", minWidth: 820 }}>
            {/* Column headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: URL_COL_TEMPLATE,
                px: 3,
                py: 1,
                borderBottom: `1px solid ${SLATE[200]}`,
                bgcolor: SLATE[50],
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {!readOnly && (
                  <Checkbox
                    size="small"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAllClick}
                    sx={{ p: 0, color: SLATE[400], "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: INDIGO[500] } }}
                  />
                )}
              </Box>
              {URL_COLUMN_HEADERS.map((col) => (
                <Typography key={col} sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {col}
                </Typography>
              ))}
            </Box>

            {loading ? (
              <LoadingSpinner message="Loading URLs..." subMessage="Fetching URLs for this website" />
            ) : urlList.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 10 }}>
                <Typography sx={{ fontSize: "1.5rem", mb: 1 }}>🔗</Typography>
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900], mb: "4px" }}>No URLs found</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                  {search ? "Try adjusting your search or filters." : "Add links using the '+ Links' button above."}
                </Typography>
              </Box>
            ) : (
              urlList.map((url, idx) => (
                <UrlTableRow
                  key={url.id}
                  url={url}
                  isChecked={selectedUrls.includes(url.id)}
                  onCheckboxChange={handleCheckboxClick}
                  onRefreshDaysChange={handleOnRefreshDaysChange}
                  refreshDaysRef={refreshDaysRef}
                  defaultRefreshDay={urls.default_refresh_day}
                  inheritedTags={website.tags || []}
                  onTagsChange={handleTagsChange}
                  onViewChunks={(u) => { setSelectedChunkUrl(u); setIsChunksSidebarOpen(true); }}
                  idx={idx}
                  totalCount={urlList.length}
                  showSourceDomain={false}
                  readOnly={readOnly}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Pagination */}
        {!loading && urlList.length > 0 && (
          <Box sx={{ borderTop: `1px solid ${SLATE[200]}`, bgcolor: "#ffffff", flexShrink: 0 }}>
            <TablePagination
              component="div"
              count={urls.count || 0}
              page={paginationParams.page}
              rowsPerPage={paginationParams.page_size}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => onChangePage(p)}
              onRowsPerPageChange={(e) => onChangeRowsPerPage(parseInt(e.target.value, 10))}
              sx={{
                "& .MuiTablePagination-toolbar": { minHeight: 48 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.8125rem",
                  color: SLATE[600],
                },
              }}
            />
          </Box>
        )}
      </Drawer>

      <ExportConfirmDialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} onConfirm={handleExportConfirm} count={urls?.count} total={urls?.total} />

      {/* Chunks Sidebar */}
      <ChunksSidebar
        url={selectedChunkUrl}
        isOpen={isChunksSidebarOpen}
        onClose={() => setIsChunksSidebarOpen(false)}
        currentApp={currentApp}
        tokens={tokens}
        website={website}
      />
    </>
  );
}
